import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Send,
  RefreshCw,
  Webhook as WebhookIcon,
  Zap,
  AlertOctagon,
  CheckCircle2,
} from "lucide-react";

const STATUS_FILTERS = [
  { v: "all", label: "Todos" },
  { v: "pending", label: "Pendientes" },
  { v: "failed", label: "Fallidos" },
  { v: "delivered", label: "Entregados" },
  { v: "dead_letter", label: "Dead-letter" },
];

const STATUS_STYLE = {
  pending:     { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Pendiente" },
  failed:      { cls: "bg-red-50 text-red-700 border-red-200", label: "Fallido" },
  delivered:   { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Entregado" },
  dead_letter: { cls: "bg-red-900 text-white border-red-900", label: "Dead-letter" },
};

const EVENT_LABEL = {
  "policy.created": "Creada",
  "policy.cancelled": "Cancelada",
  "policy.dead_letter": "Dead-letter póliza",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function StatCard({ label, value, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div
        className={`mt-1 text-2xl font-bold tracking-tight ${accent || "text-slate-900"}`}
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        {value}
      </div>
    </div>
  );
}

export default function Webhooks() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/webhooks/status");
      setStats(data);
    } catch (e) {
      console.error("Webhook stats load failed", e);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      params.set("limit", "200");
      const { data } = await api.get(`/admin/webhooks?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const onResend = async (eventId) => {
    setBusyId(eventId);
    try {
      const { data } = await api.post(`/admin/webhooks/${eventId}/resend`);
      if (data.status === "delivered") toast.success("Reenviado y entregado");
      else if (data.dead_letter) toast.error("Reenviado pero quedó en dead-letter");
      else toast.warning(`Reenviado, estado: ${data.status}`);
      await Promise.all([loadItems(), loadStats()]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusyId(null); }
  };

  const onTest = async () => {
    setTesting(true);
    setLastTest(null);
    try {
      const { data } = await api.post("/admin/webhooks/test", { partner: "evolvo" });
      setLastTest(data);
      if (data.delivered) toast.success(`Webhook de prueba entregado (HTTP ${data.response_code})`);
      else toast.error(`Webhook de prueba falló: ${data.error || "sin respuesta"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setTesting(false); }
  };

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            <WebhookIcon className="w-6 h-6" /> Webhooks salientes
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Notificaciones administrativas (estado + certificado) hacia el partner. La cobertura del ciclista
            queda vinculada en la aceptación; el webhook no es lo que da cobertura.
          </p>
        </div>
        <Button
          onClick={onTest}
          disabled={testing || !stats?.evolvo_url_configured}
          data-testid="webhook-test-button"
          className="h-9 bg-slate-900 hover:bg-slate-800 text-white text-xs"
        >
          <Send className={`w-3.5 h-3.5 mr-1.5 ${testing ? "animate-pulse" : ""}`} />
          {testing ? "Enviando…" : "Enviar webhook de prueba"}
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard testId="webhook-kpi-pending"     label="Pendientes" value={stats?.pending_count ?? "—"} accent="text-amber-700" />
        <StatCard testId="webhook-kpi-failed"      label="Fallidos"   value={stats?.failed_count ?? "—"} accent="text-red-700" />
        <StatCard testId="webhook-kpi-delivered"   label="Entregados" value={stats?.delivered_count ?? "—"} accent="text-emerald-700" />
        <StatCard testId="webhook-kpi-deadletter"  label="Dead-letter" value={stats?.dead_letter_count ?? "—"} accent="text-red-900" />
      </div>

      {/* Worker bar + Evolvo URL status */}
      <div
        data-testid="webhook-worker-bar"
        className="bg-white border border-slate-200 rounded-md p-4 mb-6 flex flex-col lg:flex-row gap-3 lg:items-center justify-between"
      >
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${stats?.running ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
            <WebhookIcon className="w-3.5 h-3.5 text-slate-700" />
            <span className="font-bold tracking-wide uppercase">Worker webhooks</span>
            <span className={`font-mono ${stats?.running ? "text-emerald-700" : "text-slate-500"}`}>
              {stats?.running ? "activo" : "parado"}
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">URL Evolvo</span>
            <span className="font-mono text-slate-900" data-testid="evolvo-url-status">
              {stats?.evolvo_url_configured ? (
                <span className="text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  {stats?.evolvo_url_masked}
                </span>
              ) : (
                <span className="text-amber-700">«pendiente»</span>
              )}
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">timeout</span>
            <span className="font-mono font-semibold">{stats?.timeout_seconds ?? "—"}s</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">max intentos</span>
            <span className="font-mono font-semibold">{stats?.max_attempts ?? "—"}</span>
          </div>
        </div>
      </div>

      {/* Last test result */}
      {lastTest && (
        <div
          className={`mb-6 p-4 border rounded-md ${lastTest.delivered ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}
          data-testid="webhook-test-result"
        >
          <div className="text-xs font-bold uppercase tracking-wider mb-2">
            {lastTest.delivered ? "Webhook de prueba entregado" : "Webhook de prueba falló"}
          </div>
          <div className="text-xs font-mono space-y-1 break-all">
            <div><span className="text-slate-500">event_id:</span> {lastTest.event_id}</div>
            <div><span className="text-slate-500">target:</span> {lastTest.target_url}</div>
            <div><span className="text-slate-500">http:</span> {lastTest.response_code ?? "—"}</div>
            <div><span className="text-slate-500">signature:</span> {lastTest.signature_header}</div>
            {lastTest.error && <div className="text-red-700"><span className="text-slate-500">error:</span> {lastTest.error}</div>}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="flex items-center gap-2 p-3 border-b border-slate-200">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 text-sm w-full sm:w-48" data-testid="webhook-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s.v} value={s.v} data-testid={`webhook-status-option-${s.v}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-xs"
            onClick={() => { loadItems(); loadStats(); }}
            data-testid="webhook-refresh-button"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refrescar
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Cuándo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Evento</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Rental</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Póliza</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Estado</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Intentos</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Próximo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">HTTP</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Último error</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={10} className="text-center text-sm text-slate-500 py-8" data-testid="webhook-loading">Cargando…</TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-sm text-slate-500 py-8" data-testid="webhook-empty">Sin entregas para este filtro.</TableCell></TableRow>
              )}
              {!loading && items.map((d) => {
                const st = STATUS_STYLE[d.status] || STATUS_STYLE.pending;
                return (
                  <TableRow key={d.event_id} data-testid={`webhook-row-${d.event_id}`}>
                    <TableCell className="font-mono text-[11px] text-slate-700">{fmtDate(d.created_at)}</TableCell>
                    <TableCell className="text-xs">
                      <div className="font-semibold text-slate-900">{EVENT_LABEL[d.event_type] || d.event_type}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{d.event_type}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{d.rental_id}</TableCell>
                    <TableCell className="font-mono text-[11px] text-slate-700">{d.policy_id || "—"}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.cls}`}>
                        {d.dead_letter && <AlertOctagon className="w-2.5 h-2.5" />}
                        {st.label}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs" data-testid={`webhook-attempts-${d.event_id}`}>
                      <span className={d.attempts >= 5 ? "text-red-700 font-bold" : ""}>{d.attempts}</span>
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-slate-700" data-testid={`webhook-next-${d.event_id}`}>
                      {d.dead_letter ? <span className="text-red-700">dead-letter</span> :
                        d.status === "delivered" ? "—" :
                        (d.next_retry_at ? fmtDate(d.next_retry_at) : "—")}
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-slate-700">{d.response_code ?? "—"}</TableCell>
                    <TableCell className="text-[11px] text-red-700 max-w-[220px] truncate" title={d.last_error || ""} data-testid={`webhook-error-${d.event_id}`}>
                      {d.last_error || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {d.status !== "delivered" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-[11px]"
                          onClick={() => onResend(d.event_id)}
                          disabled={busyId === d.event_id}
                          data-testid={`webhook-resend-${d.event_id}`}
                        >
                          <Zap className={`w-3 h-3 mr-1 ${busyId === d.event_id ? "animate-pulse" : ""}`} /> Reenviar
                        </Button>
                      ) : (
                        <span className="text-[10px] text-slate-400">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
